import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Input } from "@shared/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import {
  Pencil,
  Plus,
  Search,
  Calendar,
  User,
  Check,
  X,
  Wrench,
  Star,
  Zap,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { Alert, AlertDescription } from "@shared/ui/alert";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useSkills,
  useCreateSkill,
  useUpdateSkill,
  useToggleSkillStatus,
} from "../../../../hooks";
import { Skill } from "../../../../types";
import { convertToSnakeCase } from "@shared/utils/caseConverter";

// ====================== TYPES ======================

interface SkillFormData {
  name: string;
  description?: string;
}

// ====================== HOOKS ======================

// Custom hook for debounced search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ====================== COMPONENTS ======================

// Skill card component for better reusability
const SkillCard: React.FC<{
  skill: Skill;
  onEdit: (skill: Skill) => void;
  onToggleActive: (skill: Skill) => void;
  isToggling?: boolean;
}> = ({ skill, onEdit, onToggleActive, isToggling = false }) => {
  return (
    <Card className="overflow-hidden transition-all hover:shadow-md hover:border-primary/20 group">
      <CardContent className="p-0">
        <div className="p-4 border-b bg-muted/20">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-2">
              <Wrench className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <h4 className="font-medium">{skill.name}</h4>
                {skill.description && (
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                    {skill.description}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(skill)}
              className="opacity-0 group-hover:opacity-100 hover:bg-background/80"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="p-4">
          <div className="flex gap-2 items-center text-sm text-muted-foreground mb-2">
            <User className="h-3.5 w-3.5" />
            <p>{skill.createdBy}</p>
          </div>
          <div className="flex gap-2 items-center text-sm text-muted-foreground mb-4">
            <Calendar className="h-3.5 w-3.5" />
            <p>{skill.createdDate}</p>
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            <span className="text-sm">
              {skill.isActive ? (
                <span className="flex items-center gap-1.5 text-green-600 bg-green-50 dark:bg-green-950/30 px-2 py-1 rounded-full text-xs font-medium">
                  <Check className="h-3 w-3" /> Active
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-gray-600 bg-gray-50 dark:bg-gray-800/30 px-2 py-1 rounded-full text-xs font-medium">
                  <X className="h-3 w-3" /> Inactive
                </span>
              )}
            </span>
            <Switch
              checked={skill.isActive}
              onCheckedChange={() => onToggleActive(skill)}
              disabled={isToggling}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Empty state component
const EmptyState: React.FC<{
  hasSearchQuery: boolean;
  onAddNew: () => void;
}> = ({ hasSearchQuery, onAddNew }) => {
  return (
    <div className="col-span-full py-12 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
      <div className="flex flex-col items-center gap-2">
        <Search className="h-10 w-10 text-muted-foreground/50" />
        <h4>No skills found</h4>
        <p className="text-sm">
          {hasSearchQuery
            ? "Try adjusting your search or add a new skill"
            : "No skills available"}
        </p>
        <Button variant="outline" className="mt-2" onClick={onAddNew}>
          <Plus className="mr-2 h-4 w-4" /> Add New Skill
        </Button>
      </div>
    </div>
  );
};

// ====================== MAIN COMPONENT ======================

const SkillsTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentSkill, setCurrentSkill] = useState<Skill | null>(null);
  
  // Add state to track if we're in cancel mode
  const [isCanceling, setIsCanceling] = useState(false);

  // Get search query from URL
  const searchQuery = searchParams.get("search") || "";

  // Debounce the search input for better UX (but server handles the actual search)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debouncedSearchInput = useDebounce(searchInput, 500);

  // Update URL when debounced search changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (debouncedSearchInput.trim()) {
      newSearchParams.set("search", debouncedSearchInput.trim());
    } else {
      newSearchParams.delete("search");
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [debouncedSearchInput, searchParams, setSearchParams]);

  // API hooks - now using server-side search
  const { data: skills = [] } = useSkills(searchQuery);
  const createSkillMutation = useCreateSkill();
  const updateSkillMutation = useUpdateSkill();
  const toggleStatusMutation = useToggleSkillStatus();

  // Form fields configuration
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        type: "text",
        label: "Skill Name",
        placeholder: "Enter skill name...",
        required: true,
        minLength: 2,
        maxLength: 100,
        helperText: "Provide a clear, descriptive name for the skill",
      },
      {
        name: "description",
        type: "textarea",
        label: "Description",
        placeholder: "Enter skill description...",
        rows: 3,
        maxLength: 500,
        helperText: "Describe what this skill entails and its applications",
        className: "pt-2",
      },
    ],
    []
  );

  // Create form instance using FormService - following CustomerInformationStep pattern
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      description: "",
    },
  });

  // Update form when editing a skill - but not during cancel
  useEffect(() => {
    if (isCanceling) return; // Skip form updates during cancel
    
    if (currentSkill && isEditModalOpen) {
      FormService.populateForm(form, currentSkill, formFields);
    } else if (isAddModalOpen) {
      const populateData = {
        name: "",
        description: "",
      };
      FormService.populateForm(form, populateData, formFields);
    }
  }, [currentSkill, isEditModalOpen, isAddModalOpen, form, formFields, isCanceling]);

  // Handle field changes - simplified like CustomerInformationStep
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Prevent any field changes during cancel
    if (isCanceling) return;
  }, [isCanceling]);

  // Handle form submission
  const handleSubmit = async (data: SkillFormData) => {
    if (isCanceling) return; // Prevent submit during cancel
    
    try {
      if (currentSkill) {
        const updatePayload = convertToSnakeCase({
          id: currentSkill.id,
          name: data.name.trim(),
          description: data.description?.trim(),
        });
        // Update existing skill
        await updateSkillMutation.mutateAsync(updatePayload);
        setIsEditModalOpen(false);
      } else {
        const createPayload = convertToSnakeCase({
          name: data.name.trim(),
          description: data.description?.trim(),
        });
        // Create new skill
        await createSkillMutation.mutateAsync(createPayload);
        setIsAddModalOpen(false);
      }

      // Reset form state - following CustomerInformationStep pattern
      FormService.resetForm(form, formFields);
      setCurrentSkill(null);
    } catch (error) {
      // Handle error
    }
  };

  const handleReset = useCallback(() => {
    if (isCanceling) return; // Prevent reset during cancel
    FormService.resetForm(form, formFields);
  }, [form, formFields, isCanceling]);

  // Event handlers
  const handleEditClick = useCallback((skill: Skill) => {
    setCurrentSkill(skill);
    setIsEditModalOpen(true);
  }, []);

  const handleToggleActive = useCallback(
    async (skill: Skill) => {
      try {
        await toggleStatusMutation.mutateAsync({
          id: skill.id,
          isActive: !skill.isActive,
          name: skill.name,
        });
      } catch (error) {
        // Handle error
      }
    },
    [toggleStatusMutation]
  );

  // Handle modal close with cancel protection
  const handleAddModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsAddModalOpen(false);
    setCurrentSkill(null);
    
    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  const handleEditModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsEditModalOpen(false);
    setCurrentSkill(null);
    
    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    []
  );

  return (
    <div className="">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background pt-2 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start flex-col pb-4">
          <h3 className="page-subheading">Skills</h3>
          <p className="page-subtitle">Define and manage employee skills.</p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          disabled={createSkillMutation.isPending}
        >
          {createSkillMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Add New Skill
        </Button>
      </div>

      {/* Grid */}
      <div className="relative pb-4">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search skills..."
              className="pl-8 w-full sm:w-[250px]"
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.isArray(skills) &&
          skills.map((skill) => (
            <SkillCard
              key={skill.id}
              skill={skill}
              onEdit={handleEditClick}
              onToggleActive={handleToggleActive}
              isToggling={toggleStatusMutation.isPending}
            />
          ))}

        {Array.isArray(skills) && skills.length === 0 && (
          <EmptyState
            hasSearchQuery={!!searchQuery}
            onAddNew={() => setIsAddModalOpen(true)}
          />
        )}
      </div>

      {/* Add Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={handleAddModalClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New Skill</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={createSkillMutation.isPending}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleAddModalClose}
                    disabled={createSkillMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={createSkillMutation.isPending}
                  >
                    {createSkillMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Save Skill"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={handleEditModalClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Skill</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={updateSkillMutation.isPending}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleEditModalClose}
                    disabled={updateSkillMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={updateSkillMutation.isPending}
                  >
                    {updateSkillMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Skill"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SkillsTab;